import React, { useEffect, useRef } from 'react';
import { AtSign, Users, Building2 } from 'lucide-react';
import { handleAvatarError } from '../types';
import { resolveAvatarUrl } from '../utils/serverUrl';

/**
 * 멘션 자동완성 후보 (ChatWindow가 소스 병합·필터·키보드 내비게이션을 관장하고
 * 이 컴포넌트는 플랫 리스트를 섹션 구분해 그리는 표시 전용)
 *
 * 섹션 순서: 채널 멤버 → 특수(@all) → 부서 → 조직도 비멤버
 */
export type MentionCandidate =
  | { kind: 'member'; userId: string; name: string; depart?: string; avatar?: string }
  | { kind: 'special'; special: 'all' }
  | { kind: 'dept'; name: string; userIds: string[]; memberCount: number }
  | { kind: 'outsider'; userId: string; name: string; depart?: string; avatar?: string };

const KIND_LABELS: Record<MentionCandidate['kind'], string> = {
  member: '채널 멤버',
  special: '특수 멘션',
  dept: '부서',
  outsider: '조직도 (멤버 아님)',
};

const SPECIAL_DESC: Record<'all', string> = {
  all: '대화방 참여자 전원에게 알림 (뮤트 우회)',
};

interface MentionAutocompleteProps {
  candidates: MentionCandidate[];
  activeIndex: number;
  onSelect: (candidate: MentionCandidate) => void;
  onHoverIndex: (index: number) => void;
  darkMode?: boolean;
  /** 멤버 목록을 아직 받는 중 — 빈 결과와 구분해 안내 */
  loading?: boolean;
}

export const MentionAutocomplete: React.FC<MentionAutocompleteProps> = ({
  candidates,
  activeIndex,
  onSelect,
  onHoverIndex,
  darkMode,
  loading = false,
}) => {
  const listRef = useRef<HTMLDivElement>(null);

  // 키보드 내비게이션 시 활성 항목이 보이도록 스크롤 추적
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-mention-idx="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (candidates.length === 0) {
    if (!loading) return null;
    return (
      <div role="status" className={`absolute bottom-full left-0 mb-2 w-72 max-w-[calc(100vw-2rem)] rounded-xl shadow-lg z-50 px-3 py-2.5 text-sm ${
        darkMode ? 'bg-gray-800 border border-gray-600 text-gray-400' : 'bg-white border border-slate-200 text-slate-400'
      }`}>
        멤버 목록을 불러오는 중…
      </div>
    );
  }

  return (
    <div
      ref={listRef}
      className={`absolute bottom-full left-0 mb-2 w-72 max-w-[calc(100vw-2rem)] max-h-64 overflow-y-auto rounded-xl shadow-lg z-50 py-1 ${
        darkMode ? 'bg-gray-800 border border-gray-600' : 'bg-white border border-slate-200'
      }`}
    >
      {candidates.map((c, i) => {
        const showHeader = i === 0 || candidates[i - 1].kind !== c.kind;
        const isActive = i === activeIndex;
        const rowClass = `w-full px-3 py-1.5 flex items-center gap-2 text-left text-sm cursor-pointer ${
          isActive
            ? darkMode ? 'bg-gray-700' : 'bg-brand-100'
            : ''
        }`;
        return (
          <React.Fragment key={c.kind === 'special' ? `special:${c.special}` : c.kind === 'dept' ? `dept:${c.name}` : `${c.kind}:${c.userId}`}>
            {showHeader && (
              <div className={`px-3 pt-2 pb-1 text-[11px] font-semibold select-none ${darkMode ? 'text-gray-500' : 'text-slate-400'}`}>
                {KIND_LABELS[c.kind]}
              </div>
            )}
            <button
              type="button"
              data-mention-idx={i}
              className={rowClass}
              // click보다 먼저 발화 + textarea blur 방지
              onMouseDown={(e) => { e.preventDefault(); onSelect(c); }}
              onMouseEnter={() => onHoverIndex(i)}
            >
              {c.kind === 'special' ? (
                <>
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${darkMode ? 'bg-gray-700 text-brand-400' : 'bg-brand-100 text-brand-700'}`}>
                    <AtSign size={14} />
                  </span>
                  <span className="flex flex-col min-w-0">
                    <span className={`font-semibold ${darkMode ? 'text-gray-100' : 'text-slate-700'}`}>@{c.special}</span>
                    <span className={`text-xs truncate ${darkMode ? 'text-gray-400' : 'text-slate-400'}`}>{SPECIAL_DESC[c.special]}</span>
                  </span>
                </>
              ) : c.kind === 'dept' ? (
                <>
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${darkMode ? 'bg-gray-700 text-emerald-400' : 'bg-emerald-100 text-emerald-600'}`}>
                    <Building2 size={14} />
                  </span>
                  <span className="flex flex-col min-w-0">
                    <span className={`font-semibold truncate ${darkMode ? 'text-gray-100' : 'text-slate-700'}`}>@{c.name}</span>
                    <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-slate-400'}`}>채널 멤버 {c.memberCount}명에게 알림</span>
                  </span>
                </>
              ) : (
                <>
                  <img
                    src={resolveAvatarUrl(c.avatar)} onError={handleAvatarError}
                    alt=""
                    className="w-7 h-7 rounded-full object-cover shrink-0"
                  />
                  <span className="flex items-center gap-1.5 min-w-0 flex-1">
                    <span className={`font-medium truncate ${darkMode ? 'text-gray-100' : 'text-slate-700'}`}>{c.name}</span>
                    {c.depart && (
                      <span className={`text-xs truncate ${darkMode ? 'text-gray-400' : 'text-slate-400'}`}>{c.depart}</span>
                    )}
                  </span>
                  {c.kind === 'outsider' && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 flex items-center gap-0.5 ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-slate-100 text-slate-400'}`}>
                      <Users size={10} />멤버 아님
                    </span>
                  )}
                </>
              )}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
};
